import type { DayId } from '../core/state';
import { foot, px, type AreaKey, type Door, type MapDef, type MapId, type Rect, type WorldObject } from './mapdef';
import { home } from './maps/home';
import { school } from './maps/school';
import { store } from './maps/store';
import { town } from './maps/town';
import { sports } from './maps/sports';
import { mall } from './maps/mall';
import { grandma } from './maps/grandma';
import { bo } from './maps/bo';
import { market } from './maps/market';
import { station } from './maps/station';
import { taipei } from './maps/taipei';
import { mrt } from './maps/mrt';
import { museum } from './maps/museum';
import { hualien } from './maps/hualien';
import { OBJECTS4 } from './objects4';
import { balcony } from './maps/balcony';
import { hotel } from './maps/hotel';
import { ximen } from './maps/ximen';
import { longshan } from './maps/longshan';
import { cks } from './maps/cks';
import { beitou } from './maps/beitou';
import { hlcity } from './maps/hlcity';
import { airport } from './maps/airport';
import { kaohsiung } from './maps/kaohsiung';
import { tainan } from './maps/tainan';
import { barcelona } from './maps/barcelona';
import { OBJECTS_AIRPORT } from './objectsAirport';
import { OBJECTS_KAOHSIUNG } from './objectsKaohsiung';
import { OBJECTS_TAINAN } from './objectsTainan';
import { OBJECTS_BARCELONA } from './objectsBarcelona';
import { OBJECTS_XINYI } from './objectsXinyi';
import { OBJECTS_XIMEN } from './objectsXimen';
import { OBJECTS_CKS } from './objectsCks';
import { OBJECTS_HLCITY } from './objectsHlcity';
import { OBJECTS_TAROKO } from './objectsTaroko';
import { OBJECTS_FAMILY } from './objectsFamily';
import { OBJECTS_CLASSROOM } from './objectsClassroom';
import { OBJECTS_LIFE } from './objectsLife';
import { OBJECTS_SKY } from './objectsSky';
import { OBJECTS3 } from './objects3';
import { T, isSolidTile } from './tiles';

export type { AreaKey, Door, MapDef, MapId, Rect, WorldObject } from './mapdef';
export type { ObjectId, LabelKey } from './mapdef';

export const MAPS: Record<MapId, MapDef> = { town, school, home, store, sports, mall, grandma, bo, market, station, taipei, mrt, museum, hualien, balcony, hotel, ximen, longshan, cks, beitou, hlcity, airport, kaohsiung, tainan, barcelona };

/** Where you appear on maps reached by train or MRT rather than through a door. */
export const ARRIVALS: Partial<Record<MapId, { x: number; y: number; facing: 'up' | 'down' | 'left' | 'right' }>> = {
  taipei: taipei.spots!.train,
  museum: { x: px(13) + 8, y: px(13), facing: 'up' },
  hualien: hualien.spots!.taroko,
  mrt: { x: px(13), y: px(9) + 4, facing: 'up' },
  ximen: ximen.spots!.mrt,
  longshan: longshan.spots!.mrt,
  cks: cks.spots!.mrt,
  beitou: beitou.spots!.mrt,
  hlcity: hlcity.spots!.train,
  airport: airport.spots!.mrt,
  kaohsiung: kaohsiung.spots!.train,
  tainan: tainan.spots!.train,
  barcelona: barcelona.spots!.airport,
};

/** Interactive objects, placed on their maps. */
export const OBJECTS: readonly WorldObject[] = [
  // Counseling room
  { id: 'shelfL', map: 'school', rect: foot(1, 1, 2), solid: true, label: 'shelf', icon: '📚' },
  { id: 'shelfR', map: 'school', rect: foot(11, 1, 2), solid: true, label: 'shelf', icon: '📖' },
  { id: 'desk', map: 'school', rect: foot(3, 3, 4, 12), solid: true, label: 'desk', icon: '🖥️' },
  { id: 'plant', map: 'school', rect: foot(1, 4, 1, 8), solid: true, label: 'plant', icon: '🪴' },
  { id: 'tea', map: 'school', rect: foot(1, 7, 3, 12), solid: true, label: 'tea', icon: '🍵' },
  { id: 'water', map: 'school', rect: foot(12, 6, 1, 10), solid: true, label: 'water', icon: '🚰' },
  { id: 'sofa', map: 'school', rect: foot(8, 10, 4, 12), solid: true, label: 'sofa', icon: '🛋️' },
  // Rest of the school
  { id: 'chineseDesk', map: 'school', rect: foot(25, 7, 2, 8), solid: true, label: 'chineseDesk', icon: '✍️' },
  { id: 'atlasDesk', map: 'school', rect: foot(16, 11, 2, 8), solid: true, label: 'atlasDesk', icon: '🪑' },
  { id: 'libShelf', map: 'school', rect: foot(39, 5, 3, 10), solid: true, label: 'libShelf', icon: '📚' },
  { id: 'board', map: 'school', rect: foot(10, 13, 3, 6), solid: true, label: 'board', icon: '📋' },
  { id: 'vending', map: 'school', rect: foot(42, 13, 1, 10), solid: true, label: 'vending', icon: '🥤' },
  { id: 'easel', map: 'school', rect: foot(3, 20, 2, 8), solid: true, label: 'easel', icon: '🎨' },
  { id: 'piano', map: 'school', rect: foot(50, 19, 4, 14), solid: true, label: 'piano', icon: '🎹' },
  { id: 'nurseDesk', map: 'school', rect: foot(51, 9, 3, 12), solid: true, label: 'nurseDesk', icon: '🩺' },
  { id: 'canteen', map: 'school', rect: foot(31, 18, 5, 12), solid: true, label: 'canteen', icon: '🍱' },
  { id: 'toilet', map: 'school', rect: foot(39, 18, 4, 12), solid: true, label: 'toilet', icon: '🚻' },
  // Town
  { id: 'benchL', map: 'town', rect: foot(39, 13, 3, 6), solid: true, label: 'bench', icon: '🪑' },
  { id: 'benchR', map: 'town', rect: foot(33, 36, 3, 6), solid: true, label: 'bench', icon: '🪑' },
  { id: 'picnic', map: 'town', rect: { x: px(40), y: px(39), w: px(4), h: px(2) }, solid: false, label: 'picnic', icon: '🧺', days: ['sat'] },
  { id: 'field', map: 'town', rect: { x: px(9), y: px(13), w: px(3), h: px(2) }, solid: false, label: 'field', icon: '⚾' },
  { id: 'busStop', map: 'town', rect: foot(36, 22, 3, 6), solid: true, label: 'busStop', icon: '🚏' },
  { id: 'busStopW', map: 'town', rect: foot(7, 22, 3, 6), solid: true, label: 'busStop', icon: '🚏' },
  { id: 'busStopE', map: 'town', rect: foot(76, 22, 3, 6), solid: true, label: 'busStop', icon: '🚏' },
  { id: 'mailbox', map: 'town', rect: foot(31, 54, 1, 6, 4), solid: true, label: 'mailbox', icon: '📮' },
  { id: 'pond', map: 'town', rect: { x: px(38), y: px(30), w: px(12), h: px(5) }, solid: false, label: 'pond', icon: '🐟' },
  { id: 'bush1', map: 'town', rect: foot(12, 40, 1, 8, 1), solid: true, label: 'bush', icon: '🌿' },
  { id: 'bush2', map: 'town', rect: foot(35, 44, 1, 8, 1), solid: true, label: 'bush', icon: '🌿' },
  { id: 'bush3', map: 'town', rect: foot(51, 38, 1, 8, 1), solid: true, label: 'bush', icon: '🌿' },
  { id: 'kiteTree', map: 'town', rect: { x: px(51) + 2, y: px(56) - 6, w: 28, h: 6 }, solid: true, label: 'kiteTree', icon: '🌳', tall: 80 },
  { id: 'swing', map: 'town', rect: foot(20, 41, 2, 6), solid: true, label: 'swing', icon: '🛝' },
  // Home
  { id: 'shoes', map: 'home', rect: foot(14, 15, 2, 8), solid: true, label: 'shoes', icon: '👟' },
  { id: 'sink', map: 'home', rect: foot(8, 1, 2, 12), solid: true, label: 'sink', icon: '🥣' },
  { id: 'oven', map: 'home', rect: foot(10, 1, 2, 12), solid: true, label: 'oven', icon: '🍰' },
  { id: 'fridge', map: 'home', rect: foot(13, 1, 1, 12), solid: true, label: 'fridge', icon: '🧊' },
  { id: 'tv', map: 'home', rect: foot(10, 9, 2, 6), solid: true, label: 'tv', icon: '📺' },
  { id: 'homeDesk', map: 'home', rect: foot(17, 1, 3, 12), solid: true, label: 'homeDesk', icon: '✏️' },
  { id: 'bed', map: 'home', rect: foot(21, 3, 2, 30), solid: true, label: 'bed', icon: '🛏️' },
  { id: 'bath', map: 'home', rect: foot(1, 2, 3, 16), solid: true, label: 'bath', icon: '🛁' },
  // Store
  { id: 'shopCounter', map: 'store', rect: foot(1, 2, 4, 12), solid: true, label: 'shopCounter', icon: '🛒' },
  // Sports centre and courts
  { id: 'pool', map: 'sports', rect: { x: px(20), y: px(3), w: px(12), h: px(10) }, solid: false, label: 'pool', icon: '🏊' },
  { id: 'gymNet', map: 'sports', rect: { x: px(2) + 4, y: px(8) - 2, w: px(14) - 8, h: 3 }, solid: true, label: 'gymNet', icon: '🏐' },
  { id: 'tennisNet', map: 'town', rect: { x: px(58) + 4, y: px(16) - 3, w: px(11) - 8, h: 3 }, solid: true, label: 'tennisNet', icon: '🎾' },
  { id: 'volleyNet', map: 'town', rect: { x: px(78) + 4, y: px(16) - 3, w: px(11) - 8, h: 3 }, solid: true, label: 'volleyNet', icon: '🏐' },
  { id: 'fountain', map: 'town', rect: { x: px(72) + 4, y: px(40), w: 24, h: 14 }, solid: true, label: 'fountain', icon: '⛲' },
  // Mall
  { id: 'cafeCounter', map: 'mall', rect: foot(1, 2, 4, 12), solid: true, label: 'cafeCounter', icon: '☕' },
  { id: 'giftCounter', map: 'mall', rect: foot(14, 2, 3, 12), solid: true, label: 'giftCounter', icon: '🎁' },
  { id: 'deptCounter', map: 'mall', rect: foot(21, 2, 3, 12), solid: true, label: 'deptCounter', icon: '👕' },
  { id: 'restCounter', map: 'mall', rect: foot(31, 2, 4, 12), solid: true, label: 'restCounter', icon: '🍜' },
  { id: 'mallFountain', map: 'mall', rect: { x: px(18) + 4, y: px(17), w: 24, h: 14 }, solid: true, label: 'fountain', icon: '⛲' },
  ...OBJECTS3,
  ...OBJECTS4,
  ...OBJECTS_XINYI,
  ...OBJECTS_XIMEN,
  ...OBJECTS_CKS,
  ...OBJECTS_HLCITY,
  ...OBJECTS_TAROKO,
  ...OBJECTS_AIRPORT,
  ...OBJECTS_KAOHSIUNG,
  ...OBJECTS_TAINAN,
  ...OBJECTS_BARCELONA,
  ...OBJECTS_FAMILY,
  ...OBJECTS_CLASSROOM,
  ...OBJECTS_LIFE,
  ...OBJECTS_SKY,
];

/** Which festival is on right now (set by the festival module; none by default). */
let festivalSource: () => string | null = () => null;
export function setFestivalSource(f: () => string | null): void {
  festivalSource = f;
}
export function festivalNow(): string | null {
  return festivalSource();
}

export function objectsOn(map: MapId, day: DayId): WorldObject[] {
  const fest = festivalSource();
  return OBJECTS.filter((o) => o.map === map && (!o.days || o.days.includes(day)) && (!o.fest || o.fest === fest));
}

export function getObject(id: string): WorldObject | undefined {
  return OBJECTS.find((o) => o.id === id);
}

export function tileAt(map: MapDef, tx: number, ty: number): string {
  if (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h) return ' ';
  return map.rows[ty][tx];
}

/** Door tiles that lead nowhere (neighbours' houses) behave like walls. */
export function tileBlocks(map: MapDef, tx: number, ty: number): boolean {
  const ch = tileAt(map, tx, ty);
  if (ch === 'D') return !map.doors.some((d) => d.tiles.some(([x, y]) => x === tx && y === ty));
  return isSolidTile(ch);
}

export function doorAt(map: MapDef, x: number, y: number): Door | null {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  return map.doors.find((d) => d.tiles.some(([dx, dy]) => dx === tx && dy === ty)) ?? null;
}

export function areaAt(map: MapDef, x: number, y: number): AreaKey {
  return (map.areas.find((a) => rectContains(a.rect, x, y)) ?? map.areas[0]).key;
}

/** Where each day begins. */
export const DAY_SPAWN: Record<'school' | 'home', { map: MapId; x: number; y: number }> = {
  school: { map: 'school', x: px(7) + 8, y: px(5) },
  home: { map: 'home', x: px(19) + 8, y: px(5) + 4 },
};

/** Where you wake up after collapsing. */
export const REST_SPOTS: Record<'school' | 'home', { map: MapId; x: number; y: number }> = {
  school: { map: 'school', x: px(10), y: px(11) + 10 },
  home: { map: 'home', x: px(11), y: px(15) + 2 },
};

export function rectContains(r: Rect, x: number, y: number, pad = 0): boolean {
  return x >= r.x - pad && x <= r.x + r.w + pad && y >= r.y - pad && y <= r.y + r.h + pad;
}

/** Distance from a point to the closest point of a rect (0 if inside). */
export function rectDistance(r: Rect, x: number, y: number): number {
  const dx = Math.max(r.x - x, 0, x - (r.x + r.w));
  const dy = Math.max(r.y - y, 0, y - (r.y + r.h));
  return Math.hypot(dx, dy);
}

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export function mapSize(map: MapDef): { w: number; h: number } {
  return { w: map.w * T, h: map.h * T };
}
